import { useEffect, useState } from 'react';
import LeafletBasicMap from '@/components/Map/LeafletBasicMap';
import MapSection from '@/components/Map/MapSection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Users, Heart, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/TranslationContext';
import mapHeroBg from '@/assets/map-hero-bg.jpg';

const Map = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sosStats, setSosStats] = useState({
    total: 0,
    open: 0,
    resolved: 0,
    helpers: 0
  });
  const [locationsCount, setLocationsCount] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    document.title = 'Mappa Community | AstroPet';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Mappa OpenStreetMap con SOS per animali smarriti, luoghi pet-friendly e community AstroPet.');
    const linkCanonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    linkCanonical.setAttribute('rel', 'canonical');
    linkCanonical.setAttribute('href', window.location.origin + '/app/map');
    if (!linkCanonical.parentElement) document.head.appendChild(linkCanonical);

    loadSOSStats();
    loadLocationsCount();
    
    // Try to get user location for distance calculations
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation not available:', error);
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  }, []);

  const loadSOSStats = async () => {
    try {
      const { data, error } = await supabase
        .from('sos_reports')
        .select('status, helper_count');
      
      if (error) throw error;
      
      if (data) {
        const total = data.length;
        const open = data.filter(r => r.status === 'open').length;
        const resolved = data.filter(r => r.status === 'resolved').length;
        const helpers = data.reduce((sum, r) => sum + r.helper_count, 0);
        
        setSosStats({ total, open, resolved, helpers });
      }
    } catch (error) {
      console.error('Error loading SOS stats:', error);
    }
  };

  const loadLocationsCount = async () => {
    try {
      const { count, error } = await supabase
        .from('locations')
        .select('*', { count: 'exact', head: true })
        .eq('is_public', true);
      
      if (error) throw error;
      setLocationsCount(count || 0);
    } catch (error) {
      console.error('Error loading locations count:', error);
    }
  };

  const handleMarkerClick = (type: string, item: any) => {
    switch (type) {
      case 'adoption':
        navigate('/app/adoption');
        break;
      case 'event':
        navigate('/app/events');
        break;
      case 'sos':
        navigate('/app/sos');
        break;
      case 'user':
        navigate(`/app/profile/${item.username}`);
        break;
      default:
        break;
    }
  };

  const handleItemClick = (type: string, item: any) => {
    handleMarkerClick(type, item);
  };

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${mapHeroBg})` }}
    >
      <div className="max-w-7xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold gradient-text mb-2 font-space flex items-center">
          <MapPin className="w-8 h-8 mr-3 text-primary" />
          {t('map.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('map.description')}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass shadow-card border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('map.openCases')}</p>
                <p className="text-2xl font-bold">{sosStats.open}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass shadow-card border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('map.reportedPlaces')}</p>
                <p className="text-2xl font-bold">{locationsCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass shadow-card border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('map.helpingPeople')}</p>
                <p className="text-2xl font-bold">{sosStats.helpers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass shadow-card border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('map.helpedPets')}</p>
                <p className="text-2xl font-bold">{sosStats.resolved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map Container */}
      <Card className="glass shadow-card border-white/10 overflow-hidden">
        <CardContent className="p-0">
          <LeafletBasicMap 
            onMarkerClick={handleMarkerClick}
          />
        </CardContent>
      </Card>

      {/* Map Sections */}
      <div className="mt-6">
        <MapSection 
          userLocation={userLocation} 
          onItemClick={handleItemClick}
        />
      </div>

      {/* Instructions */}
      <Card className="glass shadow-card border-white/10 mt-6">
        <CardHeader>
          <CardTitle className="text-lg">{t('map.howToUse')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">🗺️ {t('map.navigation')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Usa il mouse per spostare la mappa</li>
                <li>• Scorri per aumentare/diminuire lo zoom</li>
                <li>• Clicca "La mia posizione" per geolocalizzarti</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">📍 {t('map.addPoints')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Clicca in qualsiasi punto della mappa</li>
                <li>• Compila il modulo con titolo e categoria</li>
                <li>• Scegli se rendere pubblico il punto</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">🏷️ {t('map.categories')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• 🏥 Veterinari e cliniche</li>
                <li>• 🌳 Parchi e aree verdi</li>
                <li>• 🛍️ Negozi per animali</li>
                <li>• ❤️ Centri adozione</li>
                <li>• 🐱 Colonie feline</li>
                <li>• 🏠 Canili</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">🔍 {t('map.emergencies')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Segnala animali smarriti</li>
                <li>• Trova rifugi nelle vicinanze</li>
                <li>• Condividi la posizione con altri</li>
                <li>• Partecipa a eventi pet-friendly</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Map;